import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useResourceList, useResourceMutations } from "@/hooks/use-admin-resource";
import { AdminPageHeader, RowActions, ResponsiveTableWrap, AdminEmptyState, slugify } from "@/components/admin/crud-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ImportDefaultsButton } from "@/components/admin/import-defaults-button";
import { buildKbCategorySeeds, buildKbArticleSeeds } from "@/lib/admin-seeds";

export const Route = createFileRoute("/_authenticated/admin/kb")({ component: KbAdmin });

type Category = { id: string; name: string; slug: string; description: string | null; sort_order: number; parent_id: string | null };
type Article = {
  id: string; category_id: string | null; title: string; slug: string; excerpt: string | null;
  content: string; published: boolean; views: number; helpful_count: number; updated_at: string;
};

function KbAdmin() {
  return (
    <div className="space-y-6">
      <AdminPageHeader title="Knowledge base" description="Help articles, organized by category." />
      <Tabs defaultValue="articles">
        <PillTabsList search>
          <PillTabsTrigger value="articles">Articles</PillTabsTrigger>
          <PillTabsTrigger value="categories">Categories</PillTabsTrigger>
        </PillTabsList>
        <TabsContent value="articles" className="mt-4"><ArticlesPanel /></TabsContent>
        <TabsContent value="categories" className="mt-4"><CategoriesPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function CategoriesPanel() {
  const { data, isLoading } = useResourceList<Category>("kb_categories", "sort_order", true);
  const { create, update, remove } = useResourceMutations("kb_categories");
  const [editing, setEditing] = useState<Category | null>(null);
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <ImportDefaultsButton table="kb_categories" onConflict="slug" loadRows={buildKbCategorySeeds} />
        <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }}>New category</Button>
      </div>
      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Slug</TableHead><TableHead>Order</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>}
            {!isLoading && !data?.length && <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground">No categories yet.</TableCell></TableRow>}
            {data?.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-muted-foreground">{c.slug}</TableCell>
                <TableCell>{c.sort_order}</TableCell>
                <TableCell className="text-right">
                  <RowActions onEdit={() => { setEditing(c); setOpen(true); }} onDelete={() => { if (confirm("Delete?")) remove.mutate(c.id); }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <CategoryDialog
        key={editing?.id ?? "new"}
        open={open} onOpenChange={setOpen} editing={editing}
        all={data ?? []}
        onSubmit={(v: any) => {
          if (editing) update.mutate({ id: editing.id, values: v }, { onSuccess: () => setOpen(false) });
          else create.mutate(v, { onSuccess: () => setOpen(false) });
        }}
      />
    </div>
  );
}

function kbDescendantIds(all: Category[], id: string): Set<string> {
  const out = new Set<string>([id]);
  let added = true;
  while (added) {
    added = false;
    for (const c of all) {
      if (c.parent_id && out.has(c.parent_id) && !out.has(c.id)) {
        out.add(c.id); added = true;
      }
    }
  }
  return out;
}

function CategoryDialog({ open, onOpenChange, editing, all, onSubmit }: any) {
  const [name, setName] = useState(editing?.name ?? "");
  const [slug, setSlug] = useState(editing?.slug ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [order, setOrder] = useState<number>(editing?.sort_order ?? 0);
  const [parentId, setParentId] = useState<string>(editing?.parent_id ?? "none");
  const blocked = editing ? kbDescendantIds(all ?? [], editing.id) : new Set<string>();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2"><Label>Name</Label><Input value={name} onChange={(e) => { setName(e.target.value); if (!editing) setSlug(slugify(e.target.value)); }} /></div>
          <div className="grid gap-2"><Label>Slug</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} /></div>
          <div className="grid gap-2">
            <Label>Parent category</Label>
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger><SelectValue placeholder="None (root)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None (root) —</SelectItem>
                {((all ?? []) as Category[]).filter((c) => !blocked.has(c.id)).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2"><Label>Description</Label><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <div className="grid gap-2"><Label>Sort order</Label><Input type="number" value={order} onChange={(e) => setOrder(Number(e.target.value))} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({ name, slug, description: description || null, sort_order: order, parent_id: parentId === "none" ? null : parentId })}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArticlesPanel() {
  const { data: articles, isLoading } = useResourceList<Article>("kb_articles", "updated_at", false);
  const { data: categories } = useResourceList<Category>("kb_categories", "sort_order", true);
  const { create, update, remove } = useResourceMutations("kb_articles");
  const [editing, setEditing] = useState<Article | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  const catName = (id: string | null) => categories?.find((c) => c.id === id)?.name ?? "—";

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (articles ?? []).filter((a) => {
      if (categoryFilter !== "all" && a.category_id !== categoryFilter) return false;
      if (!q) return true;
      return [a.title, a.slug, a.excerpt].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [articles, search, categoryFilter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant={categoryFilter === "all" ? "default" : "outline"} onClick={() => setCategoryFilter("all")}>
            All ({articles?.length ?? 0})
          </Button>
          {categories?.slice(0, 6).map((c) => (
            <Button key={c.id} size="sm" variant={categoryFilter === c.id ? "default" : "outline"} onClick={() => setCategoryFilter(c.id)}>
              {c.name}
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          <ImportDefaultsButton table="kb_articles" onConflict="slug" loadRows={buildKbArticleSeeds} />
          <Button size="sm" onClick={() => { setEditing(null); setOpen(true); }}>New article</Button>
        </div>
      </div>
      <div className="relative">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search articles by title, slug or excerpt…"
          className="h-9 text-sm"
        />
      </div>
      {!isLoading && !rows.length ? (
        <AdminEmptyState title={search || categoryFilter !== "all" ? "No articles match" : "No articles yet"} />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <ResponsiveTableWrap>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden md:table-cell">Category</TableHead>
                  <TableHead className="hidden sm:table-cell">Views</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>}
                {rows.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium max-w-md">
                      <div className="truncate">{a.title}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground md:hidden">{catName(a.category_id)}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{catName(a.category_id)}</TableCell>
                    <TableCell className="hidden sm:table-cell">{a.views}</TableCell>
                    <TableCell><Badge variant={a.published ? "default" : "secondary"}>{a.published ? "Live" : "Draft"}</Badge></TableCell>
                    <TableCell className="text-right">
                      <RowActions onEdit={() => { setEditing(a); setOpen(true); }} onDelete={() => { if (confirm("Delete?")) remove.mutate(a.id); }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ResponsiveTableWrap>
        </div>
      )}
      <ArticleDialog
        key={editing?.id ?? "new"}
        open={open} onOpenChange={setOpen} editing={editing} categories={categories ?? []}
        onSubmit={(v: any) => {
          if (editing) update.mutate({ id: editing.id, values: v }, { onSuccess: () => setOpen(false) });
          else create.mutate(v, { onSuccess: () => setOpen(false) });
        }}
      />
    </div>
  );
}

function ArticleDialog({ open, onOpenChange, editing, categories, onSubmit }: any) {
  const [title, setTitle] = useState(editing?.title ?? "");
  const [slug, setSlug] = useState(editing?.slug ?? "");
  const [excerpt, setExcerpt] = useState(editing?.excerpt ?? "");
  const [content, setContent] = useState(editing?.content ?? "");
  const [categoryId, setCategoryId] = useState<string>(editing?.category_id ?? "none");
  const [published, setPublished] = useState(editing?.published ?? false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{editing ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 py-2 max-h-[70vh] overflow-y-auto">
          <div className="grid gap-2"><Label>Title</Label><Input value={title} onChange={(e) => { setTitle(e.target.value); if (!editing) setSlug(slugify(e.target.value)); }} /></div>
          <div className="grid gap-2"><Label>Slug</Label><Input value={slug} onChange={(e) => setSlug(e.target.value)} /></div>
          <div className="grid gap-2">
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {categories.map((c: Category) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2"><Label>Excerpt</Label><Textarea rows={2} value={excerpt} onChange={(e) => setExcerpt(e.target.value)} /></div>
          <div className="grid gap-2"><Label>Content (Markdown)</Label><Textarea rows={10} value={content} onChange={(e) => setContent(e.target.value)} className="font-mono text-sm" /></div>
          <div className="flex items-center gap-3"><Switch checked={published} onCheckedChange={setPublished} /><Label>Published</Label></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit({ title, slug, excerpt: excerpt || null, content, category_id: categoryId === "none" ? null : categoryId, published })}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
